import type { ReactNode } from "react";
import Icon3D from "@/components/base/Icon3D";

export function Field({
  label,
  hint,
  children,
}: {
  label?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="block">
      {label ? (
        <span className="mb-1.5 block text-[12px] font-medium text-foreground-600">{label}</span>
      ) : null}
      {children}
      {hint ? <span className="mt-1 block text-[11px] text-foreground-400">{hint}</span> : null}
    </div>
  );
}

const boxClass =
  "flex items-center gap-2 rounded-2xl border border-background-200 bg-background-50 px-4 py-3 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100";

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "date";
  icon?: string;
  prefix?: string;
}

export function TextInput({ value, onChange, placeholder, type = "text", icon, prefix }: TextInputProps) {
  return (
    <div className={boxClass}>
      {icon ? <i className={`${icon} text-[16px] text-foreground-400`} /> : null}
      {prefix ? <span className="text-[15px] text-foreground-500">{prefix}</span> : null}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        className="w-full bg-transparent text-[15px] text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
      />
    </div>
  );
}

interface NumberInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  align?: "left" | "right";
}

export function NumberInput({
  value,
  onChange,
  placeholder,
  prefix,
  suffix,
  align = "left",
}: NumberInputProps) {
  const handle = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    const normalized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
    onChange(normalized);
  };

  return (
    <div className={boxClass}>
      {prefix ? <span className="text-[15px] text-foreground-500">{prefix}</span> : null}
      <input
        value={value}
        onChange={(e) => handle(e.target.value)}
        inputMode="decimal"
        placeholder={placeholder}
        className={`w-full bg-transparent text-[15px] tabular-nums text-foreground-950 placeholder:text-foreground-400 focus:outline-none ${
          align === "right" ? "text-right" : ""
        }`}
      />
      {suffix ? <span className="text-[12px] text-foreground-500">{suffix}</span> : null}
    </div>
  );
}

interface TextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
}

export function TextArea({ value, onChange, placeholder, maxLength = 500, rows = 3 }: TextAreaProps) {
  return (
    <div className="rounded-2xl border border-background-200 bg-background-50 px-4 py-3 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className="w-full resize-none bg-transparent text-[14px] text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
      />
      <div className="mt-1 text-right text-[10px] text-foreground-400">
        {value.length}/{maxLength}
      </div>
    </div>
  );
}

interface CurrencySegmentProps {
  value: string;
  onChange: (value: string) => void;
}

const currencies = [
  { key: "DOP", label: "RD$" },
  { key: "USD", label: "US$" },
];

export function CurrencySegment({ value, onChange }: CurrencySegmentProps) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {currencies.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onChange(c.key)}
          className={`rounded-2xl border px-3 py-2.5 text-[14px] font-semibold transition-colors ${
            value === c.key
              ? "border-primary-400 bg-primary-50 text-primary-800"
              : "border-background-200 bg-background-50 text-foreground-500"
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

interface ChipGroupProps {
  options: { key: string; label: string; icon?: string }[];
  value: string;
  onChange: (key: string) => void;
  columns?: number;
}

export function ChipGroup({ options, value, onChange, columns = 2 }: ChipGroupProps) {
  return (
    <div className={`grid gap-2 ${columns === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`flex items-center justify-center gap-1.5 rounded-2xl border px-3 py-2.5 text-[12px] font-medium transition-colors ${
            value === opt.key
              ? "border-primary-400 bg-primary-50 text-primary-800"
              : "border-background-200 bg-background-50 text-foreground-600"
          }`}
        >
          {opt.icon ? (
            <Icon3D
              icon={opt.icon}
              tone={value === opt.key ? "primary" : "neutral"}
              size={18}
              className={value === opt.key ? "" : "opacity-70"}
            />
          ) : null}
          <span className="truncate">{opt.label}</span>
        </button>
      ))}
    </div>
  );
}

export function PrimaryButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-500 py-3.5 text-[15px] font-semibold text-background-50 shadow-card transition-transform active:scale-[0.99] disabled:opacity-40"
    >
      {icon ? <i className={`${icon} text-lg`} /> : null}
      {label}
    </button>
  );
}

export function GhostButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-primary-300 bg-primary-50/60 py-3 text-[14px] font-medium text-primary-700 active:scale-[0.99]"
    >
      {icon ? <i className={`${icon} text-lg`} /> : null}
      {label}
    </button>
  );
}